import { platformApiFetch } from '@/services/platformApi/client';
import type {
  AdminReviewsAction,
  DeleteAdminReviewRequest,
  DeleteAdminReviewResponse,
  GetReportedReviewsBundleResponse,
  ListAdminReviewsResponse,
  ResolveReviewReportRequest,
  ResolveReviewReportResponse,
  UpdateAdminReviewVisibilityRequest,
  UpdateAdminReviewVisibilityResponse,
  PurgeOrphanedReviewReportsResponse,
} from '@/types/api';
const invokeAdminReviews = async <T>(action: AdminReviewsAction): Promise<T> => {
  switch (action.action) {
    case 'list':
      return platformApiFetch<T>(`/v1/admin/reviews?reportedOnly=${Boolean(action.payload?.reportedOnly)}&limit=${action.payload?.limit ?? 200}`);
    case 'reportedBundle':
      return platformApiFetch<T>('/v1/admin/reviews/reported-bundle');
    case 'setVisibility':
      return platformApiFetch<T>(`/v1/admin/reviews/${action.payload.reviewId}/visibility`, { method: 'PUT', body: JSON.stringify({ isPublic: action.payload.isPublic }) });
    case 'delete':
      return platformApiFetch<T>(`/v1/admin/reviews/${action.payload.reviewId}`, { method: 'DELETE' });
    case 'resolveReport':
      return platformApiFetch<T>(`/v1/admin/reviews/reports/${action.payload.reportId}/resolve`, { method: 'PUT', body: JSON.stringify({ resolutionNotes: action.payload.resolutionNotes }) });
    case 'purgeOrphanedReports':
      return platformApiFetch<T>('/v1/admin/reviews/purge-orphaned-reports', { method: 'POST' });
  }
};

const buildRequestId = () => crypto.randomUUID();
const buildIdempotencyKey = (prefix: string) => `${prefix}:${crypto.randomUUID()}`;

export const listAdminReviews = async (reportedOnly: boolean) => invokeAdminReviews<ListAdminReviewsResponse>({
  action: 'list',
  payload: { reportedOnly, limit: 200 },
  requestId: buildRequestId(),
});

export const getReportedReviewsBundle = async () => invokeAdminReviews<GetReportedReviewsBundleResponse>({
  action: 'reportedBundle',
  requestId: buildRequestId(),
});

export const setAdminReviewVisibility = async (payload: UpdateAdminReviewVisibilityRequest) => invokeAdminReviews<UpdateAdminReviewVisibilityResponse>({
  action: 'setVisibility',
  payload,
  requestId: buildRequestId(),
  idempotencyKey: buildIdempotencyKey(`admin-review-visibility:${payload.reviewId}`),
});

export const deleteAdminReview = async (payload: DeleteAdminReviewRequest) => invokeAdminReviews<DeleteAdminReviewResponse>({
  action: 'delete',
  payload,
  requestId: buildRequestId(),
  idempotencyKey: buildIdempotencyKey(`admin-review-delete:${payload.reviewId}`),
});

export const resolveReviewReport = async (payload: ResolveReviewReportRequest) => invokeAdminReviews<ResolveReviewReportResponse>({
  action: 'resolveReport',
  payload,
  requestId: buildRequestId(),
  idempotencyKey: buildIdempotencyKey(`admin-review-report-resolve:${payload.reportId}`),
});

export const purgeOrphanedReviewReports = async () => invokeAdminReviews<PurgeOrphanedReviewReportsResponse>({
  action: 'purgeOrphanedReports',
  requestId: buildRequestId(),
  idempotencyKey: buildIdempotencyKey('admin-review-report-purge'),
});